import React from 'react'
import { View, TouchableOpacity } from 'react-native'
import { AppModal as Modal } from '@components'
import { useTheme } from '@react-navigation/native'
import { Text, Icon } from '@components'
import { useLanguage } from '@context'
import getStyle from './style'

interface ConversationActionsModalProps {
  visible: boolean
  fitParent?: boolean
  onClose: () => void
  contactName: string
  onPinnedMessages: () => void
  onNotes: () => void
  onMedia: () => void
  onBlock: () => void
  onReport: () => void
}

export const ConversationActionsModal: React.FC<ConversationActionsModalProps> = ({
  visible,
  fitParent,
  onClose,
  contactName,
  onPinnedMessages,
  onNotes,
  onMedia,
  onBlock,
  onReport
}) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyle(theme)

  const normalActions = [
    {
      icon: 'pin' as const,
      label: t('messagesScreen.pinnedMessages'),
      onPress: onPinnedMessages,
    },
    {
      icon: 'note' as const,
      label: t('messagesScreen.viewNotes'),
      onPress: onNotes,
    },
    {
      icon: 'picture' as const,
      label: t('messagesScreen.viewMedia'),
      onPress: onMedia,
    }
  ]

  const dangerActions = [
    {
      icon: 'crossOctagon' as const,
      label: 'Bloquer',
      onPress: onBlock,
    },
    {
      icon: 'alertTriangle' as const,
      label: 'Signaler',
      onPress: onReport,
    }
  ]

  return fitParent ? (visible ? (
    <View style={{position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 100, justifyContent: 'flex-end', backgroundColor: theme.colors.primary + '30'}} 
      onStartShouldSetResponder={() => true}
      onResponderGrant={(evt) => {
        // Check if the touch is on the backdrop (not on modal content)
        if (evt.target === evt.currentTarget) {
          onClose();
        }
      }
    }>
      <TouchableOpacity 
        style={styles.modalContainer}
        activeOpacity={1}
      >
        {/* Header du modal */}
        <View style={styles.modalHeader}>
          <Text type="title" style={styles.modalTitle}>
            {contactName}
          </Text>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <Icon name="cross" size={20} color={theme.colors.secondary} />
          </TouchableOpacity>
        </View>

        {/* Actions normales */}
        <View style={styles.actionsContainer}>
          {normalActions.map((action, index) => (
            <TouchableOpacity
              key={index}
              style={[
                styles.actionItem,
                index < normalActions.length - 1 && styles.actionItemBorder
              ]}
              onPress={() => {
                action.onPress()
                onClose()
              }}
            >
              <Icon 
                name={action.icon} 
                size={20} 
                color={theme.colors.secondary} 
              />
              <Text 
                type="body" 
                style={styles.actionLabel}
              >
                {action.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Divider plus marqué */}
        <View style={styles.actionItemDivider} />

        {/* Actions dangereuses */}
        <View style={styles.actionsContainer}>
          {dangerActions.map((action, index) => (
            <TouchableOpacity
              key={index}
              style={[
                styles.actionItem,
                index < dangerActions.length - 1 && styles.actionItemBorder
              ]}
              onPress={() => {
                action.onPress()
                onClose()
              }}
            >
              <Icon 
                name={action.icon} 
                size={20} 
                color={theme.colors.error} 
              />
              <Text 
                type="body" 
                style={styles.dangerActionLabel}
              >
                {action.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </TouchableOpacity>
    </View>
  ) : <></>) : (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={onClose}
    >
      <TouchableOpacity 
        style={styles.modalOverlay}
        activeOpacity={1}
        onPress={onClose}
      >
        <TouchableOpacity 
          style={styles.modalContainer}
          activeOpacity={1}
        >
          {/* Header du modal */}
          <View style={styles.modalHeader}>
            <Text type="title" style={styles.modalTitle}>
              {contactName}
            </Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Icon name="cross" size={20} color={theme.colors.secondary} />
            </TouchableOpacity>
          </View>

          {/* Actions normales */}
          <View style={styles.actionsContainer}>
            {normalActions.map((action, index) => (
              <TouchableOpacity
                key={index}
                style={[
                  styles.actionItem,
                  index < normalActions.length - 1 && styles.actionItemBorder
                ]}
                onPress={() => {
                  action.onPress()
                  onClose()
                }}
              >
                <Icon 
                  name={action.icon} 
                  size={20} 
                  color={theme.colors.secondary} 
                />
                <Text 
                  type="body" 
                  style={styles.actionLabel}
                >
                  {action.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Divider plus marqué */}
          <View style={styles.actionItemDivider} />

          {/* Actions dangereuses */}
          <View style={styles.actionsContainer}>
            {dangerActions.map((action, index) => (
              <TouchableOpacity
                key={index}
                style={[
                  styles.actionItem,
                  index < dangerActions.length - 1 && styles.actionItemBorder
                ]}
                onPress={() => {
                  action.onPress()
                  onClose()
                }}
              >
                <Icon 
                  name={action.icon} 
                  size={20} 
                  color={theme.colors.error} 
                />
                <Text 
                  type="body" 
                  style={styles.dangerActionLabel}
                >
                  {action.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  )
} 